import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useApi } from "../../hooks/useApi.js";
import LoadingSpinner from "../../components/LoadingSpinner.jsx";
import ErrorBanner from "../../components/ErrorBanner.jsx";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import BackLink from "../../components/BackLink.jsx";
import ProposalReview from "../../components/coordinator/ProposalReview.jsx";

/**
 * ReviewGate — standalone coordinator review page.
 *
 * Fed content (interviews, documents) is extracted into proposals that park on
 * the client's ISP document until a coordinator approves them. Before this page,
 * that review UI existed only inside QuickStart, which can't be re-entered for an
 * existing client — so pending content was unreachable. This page surfaces the
 * gate for any client, at any time.
 */
export function summarizeApproval({ total = 0, updated = 0, sections = 0 }) {
  const parts = [];
  if (total)    parts.push(`${total} new record${total !== 1 ? "s" : ""}`);
  if (updated)  parts.push(`${updated} record${updated !== 1 ? "s" : ""} updated`);
  if (sections) parts.push(`${sections} section${sections !== 1 ? "s" : ""} rewritten`);
  return parts.length ? parts.join(" · ") : "nothing to save";
}

export default function ReviewGate() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [done, setDone] = useState(null);
  const [docIndex, setDocIndex] = useState(0);

  // profile for the client name in the breadcrumb
  const { data: profile } = useApi(`/clients/${clientId}/profile`);
  // what (if anything) is pending review
  const { data: pending, loading, error, refetch } =
    useApi(`/clients/${clientId}/pending-review`);

  const clientName = profile?.display_name || "Client";

  // D-1: the gate can hold several pending documents; step through them. Falls
  // back to the single-doc fields for back-compat if `documents` is absent.
  const gateDocs = pending?.documents
    || (pending?.pending ? [{ document_id: pending.document_id, filename: pending.filename, counts: pending.counts }] : []);
  const safeIndex = Math.min(docIndex, Math.max(0, gateDocs.length - 1));
  const currentDoc = gateDocs[safeIndex] || null;

  function handleApproved(created, result = {}) {
    const total = Object.values(created || {}).reduce((a, b) => a + b, 0);
    const updated = result.updated || 0;
    const sections = result.sections_written || 0;
    const conflicts = result.conflicts_raised || 0;
    // After approving, the just-approved doc leaves the pending set (its status
    // becomes 'reviewed'), so refetch shrinks gateDocs from the front. We do NOT
    // advance docIndex — staying at 0 lets the refetched list surface the next
    // document. Only when this was the LAST pending doc do we show the done card.
    if (gateDocs.length > 1) {
      setDocIndex(0);
      refetch?.();
      return;
    }
    setDone({ total, created, updated, sections, conflicts });
    refetch?.();
  }

  return (
    <div style={styles.page}>
      <div style={styles.inner}>
        <ClientBreadcrumb
          clientName={clientName}
          role="coordinator"
          clientId={clientId}
          trail={[{ label: "Review" }]}
        />

        <h1 style={styles.title}>Review extracted content</h1>
        <p style={styles.subtitle}>
          Content from interviews and documents is extracted into proposals and
          waits here until you approve it. Nothing goes live until you do.
        </p>

        {loading && <LoadingSpinner />}
        {error && <ErrorBanner message="Couldn't load pending content." />}

        {!loading && !error && done && (
          <div style={styles.doneCard}>
            <p style={{ fontSize: 40, margin: 0 }}>✓</p>
            {/* New rows, edits to existing records, and section prose are three
                different outcomes; "0 records created" after nine updates read
                as a failure (David, Oct 15). */}
            <h2 style={styles.doneTitle}>Approved — {summarizeApproval(done)}</h2>
            <p style={styles.doneBody}>
              {done.conflicts > 0
                ? `${done.conflicts} contradiction${done.conflicts !== 1 ? "s" : ""} to resolve in the section editors. Everything else is saved as drafts.`
                : "Saved as drafts. You can refine and publish in the section editors."}
            </p>
            <div style={styles.doneActions}>
              <button style={styles.primaryBtn}
                onClick={() => navigate(`/coordinator/clients/${clientId}/edit`)}>
                Go to section editors →
              </button>
              {pending?.pending && (
                <button style={styles.secondaryBtn} onClick={() => { setDocIndex(0); setDone(null); }}>
                  Review remaining
                </button>
              )}
            </div>
          </div>
        )}

        {!loading && !error && !done && !pending?.pending && (
          <div style={styles.emptyCard}>
            <p style={styles.emptyTitle}>Nothing to review right now</p>
            <p style={styles.emptyBody}>
              When you feed an interview or upload a document, the extracted
              proposals will appear here for your review.
            </p>
            <BackLink to={`/coordinator/clients/${clientId}/edit`} />
          </div>
        )}

        {!loading && !error && !done && pending?.pending && currentDoc && (
          <>
            {gateDocs.length > 1 && (
              <div style={styles.stepperRow}>
                <span style={styles.stepperLabel}>
                  Document {safeIndex + 1} of {gateDocs.length}
                </span>
                <span style={styles.stepperFile}>{currentDoc.filename}</span>
              </div>
            )}
            <div style={styles.countRow}>
              <CountPill n={currentDoc.counts.items} label="items" />
              <CountPill n={currentDoc.counts.goals} label="goals" />
              <CountPill n={currentDoc.counts.people} label="people" />
              <CountPill n={currentDoc.counts.routines} label="routines" />
              {currentDoc.counts.sections > 0 && <CountPill n={currentDoc.counts.sections} label="sections" />}
            </div>
            <ProposalReview
              key={currentDoc.document_id}
              clientId={clientId}
              documentId={currentDoc.document_id}
              onApproved={handleApproved}
            />
            <div style={styles.exitRow}>
              <BackLink to={`/coordinator/clients/${clientId}/edit`} />
              <span style={styles.exitHint}>
                {gateDocs.length > 1
                  ? "Leaving won't approve anything — remaining documents stay here."
                  : "Leaving won't approve anything — pending content stays here."}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CountPill({ n, label }) {
  if (!n) return null;
  return <span style={styles.countPill}><strong>{n}</strong> {label}</span>;
}

const styles = {
  page:  { minHeight: "100vh", background: "var(--color-bg-subtle)" },
  inner: { maxWidth: 720, margin: "0 auto", padding: "var(--space-6) var(--space-5)", display: "flex", flexDirection: "column", gap: "var(--space-4)" },
  title: { fontSize: "var(--text-2xl)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  subtitle: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  countRow: { display: "flex", gap: "var(--space-2)", flexWrap: "wrap" },
  stepperRow: { display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap", paddingBottom: "var(--space-1)" },
  stepperLabel: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  stepperFile: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  countPill: { fontSize: "var(--text-xs)", color: "var(--color-text-secondary)", background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-full)", padding: "3px var(--space-3)" },
  emptyCard: { background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-2)", alignItems: "flex-start" },
  emptyTitle: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", margin: 0 },
  emptyBody: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  doneCard: { background: "var(--color-bg-base)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", padding: "var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-2)", alignItems: "flex-start" },
  doneTitle: { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)", margin: 0 },
  doneBody: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: 0, lineHeight: "var(--leading-normal)" },
  doneActions: { display: "flex", gap: "var(--space-3)", marginTop: "var(--space-2)" },
  primaryBtn: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "#fff", background: "var(--color-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  secondaryBtn: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-4)", cursor: "pointer" },
  exitRow:  { display: "flex", alignItems: "center", gap: "var(--space-3)", flexWrap: "wrap", paddingTop: "var(--space-2)" },
  exitHint: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
};
